import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import EditIcon from '@mui/icons-material/Edit';
import { Avatar, Box, Divider, Grid, Stack, Typography } from '@mui/material';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { venueGetOfferApi } from '../../api/offers/venueGetOfferApi';
import { venueCreateComplaintApi } from '../../api/reservations/venueCreateComplaintApi';
import Button from '../../components/Button/Button';
import LoadingButton from '../../components/Button/LoadingButton';
import { SHIPPING_AVAILABILITY } from '../../components/Dialogs/CollabsFilters/options';
import { useConfirm } from '../../components/Dialogs/Confirm/context/ConfirmDialog.context';
import { OfferSurveyDialog } from '../../components/Dialogs/OfferSurveyDialog/OfferSurveyDialog';
import { mapToApiOfferSurveyPayload } from '../../components/Dialogs/ReportReservations/components/schema';
import { GoBackV2 } from '../../components/GoBack/GoBackV2';
import { Gallery, Item } from '../../components/Photoswipe/Photoswipe';
import StatusLabel from '../../components/StatusLabel/StatusLabel';
import ReservationsTable from '../../components/Tables/Reservation/Reservation';
import { ComplaintItemInput, DaysOfWeek, Offer, OfferCategory, OfferStatus, TipType } from '../../graphql/API';
import { useUpdateOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import { useAppSelector } from '../../redux/hooks';
import { datetimeWithTimezone, getTimeInTimezone } from '../../utils/dateV2';
import { formatNumber, formatNumberWithCommas } from '../../utils/formatter';
import { getOfferAccessValue } from '../../utils/offerAccess';
import OfferDetailSkeleton from './OfferDetailSkeleton';
import BorderBox from './components/BorderBox';
import DetailRow from './components/DetailRow';
import LabelDay from './components/LabelDay';
import Requirement from './components/Requirement';
import './style.css';

const waitTimeOptions = [
  { value: 'Everyday', key: '0' },
  { value: '3 times per week', key: '2' },
  { value: '1 time per week', key: '6' },
  { value: '1 time every two weeks', key: '13' },
  { value: '1 time per month', key: '30' },
  { value: '1 time every 2 months', key: '60' },
  { value: '1 time per year', key: '364' },
  { value: '1 time only', key: '10000' },
];

const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const OfferDetail: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const venueState = useAppSelector((state) => state.venue.value);
  const venueId = venueState?.id;
  const nagivate = useNavigate();

  const { confirm } = useConfirm();

  const [surveyPopup, setSurveyPopup] = React.useState(false);
  const [isSurveyLoading, setIsSurveyLoading] = React.useState(false);
  const updateMutation = useUpdateOfferQuery(venueId);

  const { offerId } = useParams<{ offerId: string }>();

  const { data, isLoading } = useQuery<Offer>({
    queryKey: ['offer-detail', offerId],
    queryFn: async ({ queryKey }) => {
      const [, offerID] = queryKey as [string, string];

      return venueGetOfferApi(offerID);
    },
    placeholderData: keepPreviousData,
  });

  const frequency = React.useMemo(
    () =>
      data
        ? waitTimeOptions.find((option) => option.key === String(data.waitTime))?.value
        : undefined,
    [data],
  );

  const suggestedTipLabel = React.useMemo(() => {
    if (!data?.suggestedTip || Number(data.suggestedTip) <= 0) {
      return undefined;
    }
    return data.tipType === TipType.percent
      ? `${data.suggestedTip}%`
      : `${venueState?.currency} ${data.suggestedTip}`;
  }, [data?.suggestedTip, data?.tipType, venueState?.currency]);

  const hasInstagramRequirement = React.useMemo(
    () =>
      Boolean(
        data?.postOnInstagramFeed ||
          data?.postOnInstagramStories ||
          data?.postOnInstagramStories2x ||
          data?.postOnInstagramStories3x ||
          data?.postOnInstagramReels,
      ),
    [data],
  );

  const requiredTag = React.useMemo(() => {
    if (!data) {
      return undefined;
    }
    if (data.postOnTikTokVideo) {
      return data.tag || venueState?.tiktokHandle || undefined;
    }
    return hasInstagramRequirement ? venueState?.instagramHandle || undefined : undefined;
  }, [data, hasInstagramRequirement, venueState?.tiktokHandle, venueState?.instagramHandle]);

  const sortedAvailableDays = React.useMemo(() => {
    const order: Record<string, number> = {
      [DaysOfWeek.monday]: 1,
      [DaysOfWeek.tuesday]: 2,
      [DaysOfWeek.wednesday]: 3,
      [DaysOfWeek.thursday]: 4,
      [DaysOfWeek.friday]: 5,
      [DaysOfWeek.saturday]: 6,
      [DaysOfWeek.sunday]: 7,
    };
    return [...(data?.availableDays ?? [])].sort(
      (a, b) => (order[String(a)] ?? 99) - (order[String(b)] ?? 99),
    );
  }, [data?.availableDays]);

  const handleOfferSurvey = () => {
    setSurveyPopup((prev) => !prev);
  };

  const handleOfferDelete = React.useCallback(
    async (questions: ComplaintItemInput[]) => {
      if (offerId) {
        setSurveyPopup((prev) => !prev);
        setIsSurveyLoading(true);
        const apiPayload = mapToApiOfferSurveyPayload(questions || [], {
          venueId: venueState?.id || '',
          plan: venueState?.premium ? 'PREMIUM' : 'BASIC',
          venueName: venueState?.name || '',
          email: venueState?.email || '',
          venueLink: venueState?.id || '',
          type: 'Venue canceled offer survey',
        });
        console.log(apiPayload, 'api payload');
        await venueCreateComplaintApi(apiPayload);
        await updateMutation.mutateAsync({ id: offerId, status: OfferStatus.cancelled });
        setIsSurveyLoading(false);
        enqueueSnackbar('Offer cancelled, Thank you for providing us with your valuable feedback', {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        nagivate(data?.category === OfferCategory.product ? `/products` : `/offers`);
      }
    },
    [
      offerId,
      venueState?.id,
      venueState?.premium,
      venueState?.name,
      venueState?.email,
      updateMutation,
      enqueueSnackbar,
      nagivate,
      data?.category,
    ],
  );

  const handleOfferReactive = React.useCallback(async () => {
    if (offerId) {
      const isConfirm = await confirm({
        title: 'Reactivate Offer',
        confirmationText: 'Reactivate',
        description: <>Are you sure you want to activate this offer?</>,
      });

      if (isConfirm) {
        await updateMutation.mutateAsync({ id: offerId, status: OfferStatus.active });
        nagivate(data?.category === OfferCategory.product ? `/products` : `/offers`);
      }
    }
  }, [offerId, updateMutation, nagivate, confirm, data?.category]);

  const renderButton = () => {
    if (!data) {
      return null;
    }

    if ([OfferStatus.cancelled, OfferStatus.deleted].includes(data.status)) {
      return (
        <>
          <Button
            component={Link}
            to={
              data.category === OfferCategory.product
                ? `/products/${data.id}/edit`
                : `/offers/${data.id}/edit`
            }
            id='btn-edit'
            variant='outlined'
            size='large'
            color='primary'
            sx={{ py: 1, px: 3 }}
          >
            Edit
          </Button>
          <LoadingButton
            id='btn-delete'
            size='large'
            variant='outlined'
            color='primary'
            onClick={handleOfferReactive}
            loading={updateMutation.isPending}
            sx={{ py: 1, px: 3 }}
          >
            Reactivate
          </LoadingButton>
        </>
      );
    }

    return (
      <>
        <Button
          component={Link}
          to={
            data.category === OfferCategory.product
              ? `/products/${data.id}/edit`
              : `/offers/${data.id}/edit`
          }
          id='btn-edit'
          startIcon={<EditIcon />}
          variant='outlined'
          size='medium'
          color='primary'
          sx={{ py: 1, px: 3 }}
        >
          Edit
        </Button>
        <LoadingButton
          id='btn-delete'
          variant='outlined'
          size='medium'
          onClick={handleOfferSurvey}
          color='inherit'
          loading={updateMutation.isPending || isSurveyLoading}
          sx={{ minWidth: 80, color: '#E05757', borderColor: '#E05757', py: 1, px: 3 }}
        >
          Delete
        </LoadingButton>
      </>
    );
  };

  const timezoneId = React.useMemo(() => data?.category === OfferCategory.event && data?.offerTimeZoneId ? data?.offerTimeZoneId : data?.timeZoneId, [data?.category, data?.offerTimeZoneId, data?.timeZoneId]);

  const { startDatetime, endDatetime } = React.useMemo(
    () => ({
      startDatetime: datetimeWithTimezone({
        date: data?.startDate,
        time: data?.startTime,
        timezoneId: timezoneId,
      }).tz(),
      endDatetime: datetimeWithTimezone({
        date: data?.startDate,
        time: data?.endTime,
        timezoneId: timezoneId,
      }).tz(),
    }),
    [data, timezoneId],
  );

  return (
    <Box
      px={4}
      py={2}
      sx={{
        backgroundColor: 'white',
        flex: 1,
      }}
    >
      {!isLoading && data ? (
        (() => {
          const isProduct = data.category === OfferCategory.product;
          const isEvent = data.category === OfferCategory.event;
          const isVenueOffer = !data.private && !isEvent && !isProduct;

          return (
            <Gallery>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                flexWrap='wrap'
                gap={2}
                sx={{
                  mx: -4,
                  mt: -2,
                  px: 4,
                  py: 2,
                  mb: 3,
                  backgroundColor: '#fcfcfc',
                  borderBottom: '1px solid #f1f2f1',
                }}
              >
                <Box display='flex' alignItems='center' gap={2}>
                  <GoBackV2 to={isProduct ? '/products' : '/offers'} />
                  <Item
                    itemProps={{
                      original: String(data.picture?.large || data.picture?.medium),
                      width: '1024',
                      height: '768',
                    }}
                  >
                    <Avatar
                      variant='rounded'
                      alt={String(data.title)}
                      src={String(data.picture?.medium)}
                      sx={{ height: 64, width: 88, cursor: 'pointer' }}
                    />
                  </Item>
                  <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>{data.title}</Typography>
                </Box>
                <Stack direction='row' spacing={2}>
                  {renderButton()}
                </Stack>
              </Box>

              <Grid container spacing={3} sx={{ mb: 4 }} alignItems='stretch'>
                <Grid item xs={12} md={data.private ? 12 : 4}>
                  <BorderBox title='Offer details'>
                    <DetailRow label='Name' description={data.title} />
                    <DetailRow label='Description' description={data.description} />
                    <DetailRow
                      label='Status'
                      description={
                        <StatusLabel
                          type='offer'
                          status={data.status}
                          emptyBackground
                          fontSize='13px'
                        />
                      }
                    />
                    {isProduct && (
                      <>
                        <DetailRow
                          label='Product value'
                          description={`${venueState?.currency} ${formatNumberWithCommas(
                            data?.offerValue || 0,
                          )}`}
                        />
                        <DetailRow
                          label='Incentive'
                          description={`${venueState?.currency} ${formatNumberWithCommas(
                            data?.incentiveValue || 0,
                          )}`}
                        />
                        <DetailRow
                          label='Minimum followers'
                          description={formatNumber(data?.minFollowers ?? 0)}
                        />
                        <DetailRow
                          label='Shipping availability'
                          description={data?.shippingRegions
                            ?.map((region) => region && SHIPPING_AVAILABILITY[region])
                            .join(', ')}
                        />
                      </>
                    )}
                    {isEvent && (
                      <>
                        <DetailRow label='Spots' description={String(data.spots)} />
                        <DetailRow
                          label='Event date'
                          description={`${data.availableDays
                            ?.map((day) => capitalizeWords(day || ''))
                            .join('')} ${startDatetime.format('ll')}`}
                        />
                        <DetailRow label='Event location' description={data.address} />
                        <DetailRow
                          label='Time'
                          description={`${getTimeInTimezone(
                            data?.startDate,
                            data?.startTime,
                            timezoneId,
                          )} - ${getTimeInTimezone(data?.startDate, data?.endTime, timezoneId)}`}
                        />
                      </>
                    )}
                    {!data.private && !isProduct && (
                      <>
                        <DetailRow
                          label='Lifestyle'
                          description={getOfferAccessValue(data.selectMembersAccess)}
                        />
                        <DetailRow
                          label='Beautypass Select'
                          description={getOfferAccessValue(data.selectMembersAccess)}
                        />
                      </>
                    )}
                  </BorderBox>
                </Grid>

                {isVenueOffer && (
                  <Grid item xs={12} md={4}>
                    <BorderBox title='Schedule'>
                      <DetailRow label='Offer Start' description={startDatetime.format('ll')} />
                      {data.endDate && (
                        <DetailRow label='Offer End' description={endDatetime.format('ll')} />
                      )}
                      <DetailRow
                        label='Time'
                        description={`${getTimeInTimezone(
                          data?.startDate,
                          data?.startTime,
                          timezoneId,
                        )} - ${getTimeInTimezone(data?.startDate, data?.endTime, timezoneId)}`}
                      />
                      <DetailRow label='Frequency' description={frequency} />
                      <DetailRow label='Spots' description={String(data.spots)} />
                      <DetailRow
                        label='Available Days'
                        description={
                          sortedAvailableDays.length === 7
                            ? 'Everyday'
                            : sortedAvailableDays.map((x, i) => (
                                <LabelDay key={i} day={String(x)} />
                              ))
                        }
                      />
                    </BorderBox>
                  </Grid>
                )}

                {!data.private && (
                  <Grid item xs={12} md={4}>
                    <BorderBox title='Requirements'>
                      <Requirement data={data} />
                      {requiredTag && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            py: 1,
                            borderBottom: '1px solid #E6E6E6',
                          }}
                        >
                          <AlternateEmailIcon sx={{ mr: 2, fontSize: 20 }} />
                          <Typography
                            sx={{ fontSize: '13px', color: '#111', fontFamily: 'Poppins' }}
                          >
                            @{requiredTag}
                          </Typography>
                        </Box>
                      )}
                      {!isProduct && hasInstagramRequirement && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            py: 1,
                            borderBottom: '1px solid #E6E6E6',
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '13px', color: '#111', fontFamily: 'Poppins' }}
                          >
                            Minimum followers
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: '#111',
                              fontWeight: 500,
                              fontFamily: 'Poppins',
                              ml: 1,
                            }}
                          >
                            {formatNumber(data?.minFollowers ?? 0)}
                          </Typography>
                        </Box>
                      )}
                      {suggestedTipLabel && (
                        <DetailRow label='Suggested tip' description={suggestedTipLabel} />
                      )}
                      {data.dressCode && (
                        <DetailRow label='Dress code' description={data.dressCode} />
                      )}
                    </BorderBox>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ mb: 4, borderColor: '#F2EFEF' }} />

              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography
                  component={'span'}
                  sx={{ fontSize: '18px', fontWeight: '500', mb: 3, display: 'inline-block' }}
                >
                  Reservations
                </Typography>
                <ReservationsTable filters={{ offerID: offerId }} showPromotionLimitPopup={false} />
              </Box>
            </Gallery>
          );
        })()
      ) : (
        <OfferDetailSkeleton />
      )}

      <OfferSurveyDialog
        open={surveyPopup}
        onClose={handleOfferSurvey}
        isPrivateOffer={data?.private || false}
        onSubmitQuestions={handleOfferDelete}
      />
    </Box>
  );
};

export default OfferDetail;
